// Modules
import ReactDOM from "react-dom";
import CircularProgress from "@mui/material/CircularProgress";

// custom: table, image

// Helpers
import {
  getLayoutIcons,
  getLayoutFiles,
  getDefaultBlockContent,
} from "../utils/helpers.js";

// Blocks
import Image from "../editorjs/blocks/image/index";
import Audio from "../editorjs/blocks/audio/index";
import Video from "../editorjs/blocks/video/index";
import Gallery from "../editorjs/blocks/gallery/index";
import NestedList from "../editorjs/blocks/nested-list";
import Quote from "../editorjs/blocks/quote";
import Table from "../editorjs/blocks/table";
import Title from "../editorjs/blocks/title/title";
import Subtitle from "../editorjs/blocks/subtitle/subtitle";
import BlockHeading from "../editorjs/blocks/blockHeading/blockHeading";
import Body from "../editorjs/blocks/body";
import Contact from "../editorjs/blocks/contact";
import Embed from "../editorjs/blocks/embed";
import Map from "../editorjs/blocks/map";
import Point from "../editorjs/blocks/point";
import Icon from "../editorjs/blocks/icon";
import Client from "../editorjs/blocks/client";
import InlineImage from "../editorjs/blocks/inline-image";
import Pillar from "../editorjs/blocks/pillar";
import LocationHeader from "../editorjs/blocks/hotel-blocks/location-header";
import HotelCapacity from "../editorjs/blocks/hotel-blocks/hotel-capacity";
import LocationFooter from "../editorjs/blocks/hotel-blocks/location-footer";
import HotelTextOverlay from "../editorjs/blocks/hotel-blocks/hotel-text-overlay";
import CountryMap from "../editorjs/blocks/hotel-blocks/country-map";
import Popup from "../editorjs/blocks/popup";
import ButtonGroup from "../editorjs/blocks/button-group";
import Rule from "../editorjs/blocks/horizontal-rule";
import Shape from "../editorjs/blocks/shape";
import ImageTextOverlay from "../editorjs/blocks/image-text-overlay";
import CaptionedImage from "../editorjs/blocks/captioned-image";
import Chart from "../editorjs/blocks/chart";
import DynamicMap from "../editorjs/blocks/dynamic-map";
import AtAGlance from "../editorjs/blocks/at-a-glance";

// Tunes
import AnimationTune from "../editorjs/blockTunes/animation";
import FontColorTool from "../editorjs/blockTunes/font-color-tune";
import BlockBackgroundTool from "../editorjs/blockTunes/block-bg-tune";
import HeadingToggleTool from "../editorjs/blockTunes/heading-toggle-tune";
import AlignmentBlockTune from "../editorjs/blockTunes/alignment";
import DimensionTune from "../editorjs/blockTunes/dimension";
import OverlayTool from "../editorjs/blockTunes/overlay-tool";
import DragResizeTune from "../editorjs/blockTunes/draggable/draggable";
import FontSizeTune from "../editorjs/blockTunes/font-size";

// Inline tunes
import SubscriptTool from "../editorjs/inlineTunes/subscript-tool";
import SuperscriptTool from "../editorjs/inlineTunes/superscript-tool";
import SizeTool from "../editorjs/inlineTunes/size-tool";
import H1 from "../editorjs/inlineTunes/heading-tool/h1-tool";
import H2 from "../editorjs/inlineTunes/heading-tool/h2-tool";
import H3 from "../editorjs/inlineTunes/heading-tool/h3-tool";
import H4 from "../editorjs/inlineTunes/heading-tool/h4-tool";
import H5 from "../editorjs/inlineTunes/heading-tool/h5-tool";
import H6 from "../editorjs/inlineTunes/heading-tool/h6-tool";
import Hyperlink from "../editorjs/inlineTunes/hyperlink-tool";
import PopupLink from "../editorjs/inlineTunes/popupLink-tool";
import SlideLink from "../editorjs/inlineTunes/slideLink-tool";

// Block registry map
const blockRegistry = {
  Image,
  Audio,
  Video,
  Gallery,
  NestedList,
  Quote,
  Table,
  Title,
  Subtitle,
  BlockHeading,
  Body,
  Contact,
  Embed,
  Map,
  Point,
  Icon,
  Client,
  InlineImage,
  Pillar,
  LocationHeader,
  HotelCapacity,
  LocationFooter,
  HotelTextOverlay,
  CountryMap,
  Popup,
  ButtonGroup,
  Rule,
  Shape,
  ImageTextOverlay,
  CaptionedImage,
  Chart,
  DynamicMap,
  AtAGlance,
};

// Handlers
const handlers = {
  showMediaLibrary: (block, ctx, options) => {
    if (!block || !ctx) {
      return;
    }

    ctx.setCurrentBlock(block);

    // Set media type if specified in options
    if (options?.type) {
      ctx.setMediaType(options.type);
    }

    // Pass through file restrictions if provided by the block
    if (block?.slideFiles) {
      ctx.setSlideFiles(block.slideFiles);
    }

    ctx.setShowMediaLibrary(true);
  },

  showIconSelector: (block, ctx) => {
    if (!block || !ctx) {
      return;
    }

    ctx.setCurrentBlock(block);
    ctx.setShowIconSelector(true);
  },

  showAudioLibrary: (block, ctx, options) => {
    // Wrapper that calls showMediaLibrary with audio type
    return handlers.showMediaLibrary(block, ctx, { ...options, type: "audio" });
  },

  showVideoLibrary: (block, ctx, options) => {
    // Wrapper that calls showMediaLibrary with video type
    return handlers.showMediaLibrary(block, ctx, { ...options, type: "video" });
  },

  showMediaLibraryByIndex: (block, ctx, options) => {
    if (!block || !ctx) return;

    const editorInstance = ctx.editorInstance;
    if (!editorInstance) {
      console.warn("[showMediaLibraryByIndex] No editor instance available");
      return handlers.showMediaLibrary(block, ctx, options);
    }

    // Get layout files array
    let files = getLayoutFiles(
      options?.layoutId || ctx.slideLayout?.id,
      ctx.themeLabel
    );

    // If files is an array of file configs, determine which one based on block index
    if (Array.isArray(files) && files.length > 1) {
      const blocksCount = editorInstance.blocks.getBlocksCount();
      let imageBlocks = [];

      for (let i = 0; i < blocksCount; i++) {
        const b = editorInstance.blocks.getBlockByIndex(i);
        if (b.name === (options?.blockType || "image")) {
          imageBlocks.push(b);
        }
      }

      // Find this block's index among image blocks
      const blockIndex = imageBlocks.findIndex(
        (b) => b.id === block.details?.id
      );

      if (blockIndex === 0) {
        files = files[0];
      } else if (blockIndex > 0 && files[1]) {
        files = files[1];
      }
      // If blockIndex is -1 or files doesn't have index, use full array
    }

    ctx.setSlideFiles(files);
    ctx.setCurrentBlock(block);
    ctx.setShowMediaLibrary(true);
  },
};

const renderToDomNode = (element) => {
  const container = document.createElement("div");
  ReactDOM.render(element, container);
  return container;
};

export function getTuneColors(theme_json, slideLayout) {
  return [
    ...(theme_json?.tuneColors ||
      slideLayout?.tuneColors || ["#fff", "#e9e9e9", "#777", "#000"]),
  ];
}

function getTableOptions(theme_json, slideLayout) {
  const colors = getTuneColors(theme_json, slideLayout);
  return {
    swatches:
      colors?.length > 0
        ? ["#00000000", ...colors]
        : ["#fff", "#e9e9e9", "#777", "#000"],
  };
}

function getSwatches(theme_json, slideLayout) {
  const colors = getTuneColors(theme_json, slideLayout);
  return {
    swatches:
      colors?.length > 0
        ? ["#00000000", ...colors]
        : ["#fff", "#e9e9e9", "#777", "#000"],
  };
}

// Resolve reference functions from config
function resolveRef(ref, theme_json, slideLayout, themeLabel) {
  if (!ref?.fn) return null;

  console.debug(
    `[resolveRef] Executing ${ref.fn} for layout ${slideLayout?.id}`,
    ref.args
  );

  const fn = {
    getLayoutFiles,
    getLayoutIcons,
    getDefaultBlockContent,
    getTuneColors,
    getTableOptions,
    getSwatches,
  }[ref.fn];

  if (!fn) {
    console.warn(
      `[resolveRef] Unknown reference function '${ref.fn}' for layout '${slideLayout?.id}'. Check your slide.config.json.`
    );
    return null;
  }

  try {
    switch (ref.fn) {
      case "getLayoutFiles":
        return fn(ref.args?.[0] || slideLayout?.id, themeLabel || theme_json);
      case "getLayoutIcons":
        return fn(ref.args?.[0] || slideLayout?.id, themeLabel || theme_json);
      case "getDefaultBlockContent":
        return fn(ref.args?.[0] || slideLayout?.id, ref.args?.[1], theme_json);
      case "getTuneColors":
        return fn(theme_json, slideLayout);
      case "getTableOptions":
        return fn(theme_json, slideLayout);
      case "getSwatches":
        return fn(theme_json, slideLayout);
      default:
        return fn(...(ref.args || []), theme_json, slideLayout);
    }
  } catch (err) {
    console.error(
      `[resolveRef] Failed to execute '${ref.fn}' for layout '${slideLayout?.id}':`,
      err
    );
    return null;
  }
}

// Global inline tools (shared across all layouts)
function getInlineTools(theme_json, themeDirectory, slideLayout) {
  const colorOptions =
    getTuneColors(theme_json, slideLayout)?.length > 0
      ? getTuneColors(theme_json, slideLayout)
      : ["#fff", "#e9e9e9", "#777", "#000"];

  // For font-size tune, some layouts may define enabled blocks in theme_json
  const enabledFontSizeBlocks =
    theme_json?.layouts?.find(
      (l) => l.id === slideLayout.id && l.fontSizeTune?.length > 0
    )?.fontSizeTune || null;

  return {
    Subscript: { class: SubscriptTool },
    Superscript: { class: SuperscriptTool },
    Size: { class: SizeTool },
    AlignmentBlockTune: {
      class: AlignmentBlockTune,
      config: { default: "left" },
    },
    H1: { class: H1 },
    H2: { class: H2 },
    H3: { class: H3 },
    H4: { class: H4 },
    H5: { class: H5 },
    H6: { class: H6 },

    /** Font color tune (with full theme defaults) */
    FontColorTool: {
      class: FontColorTool,
      config: {
        options: colorOptions,
        default: slideLayout?.default_font_color || "",
        // IMPORTANT: legacy passes layout *id* string, not the layout object
        layout: slideLayout.id,
        blocks: {}, // preserved from legacy (future use)
      },
    },

    /** Font size tune (layout-aware) */
    FontSizeTune: {
      class: FontSizeTune,
      config: {
        enabledBlocks: enabledFontSizeBlocks,
      },
    },

    /** Full animation tune with label options */
    Animation: {
      class: AnimationTune,
      config: {
        animations: [
          { class: "", label: "none" },
          { class: "fade-out", label: "fade-out" },
          { class: "fade-up", label: "fade-up" },
          { class: "fade-down", label: "fade-down" },
          { class: "fade-left", label: "fade-left" },
          { class: "fade-right", label: "fade-right" },
          { class: "grow", label: "grow" },
        ],
      },
    },

    /** Hyperlink-related tunes (same color options as theme) */
    Hyperlink: {
      class: Hyperlink,
      config: { options: colorOptions },
    },
    PopupLink: {
      class: PopupLink,
      config: { options: colorOptions },
    },
    SlideLink: {
      class: SlideLink,
      config: { options: colorOptions },
    },

    /** Dimension tune (no config) */
    Dimension: { class: DimensionTune },

    /** Block background tune (full color + pattern config) */
    BlockBackgroundTool: {
      class: BlockBackgroundTool,
      config: {
        options: [
          { name: "blue", value: "#3573cb" },
          { name: "green", value: "#449479" },
          { name: "purple", value: "#bd53ae" },
          { name: "red", value: "#a22e3e" },
          { name: "yellow", value: "#f7d74a" },
        ],
        patterns: theme_json?.patterns || [],
        themeDirectory,
        default: { name: "blue", value: "#3573cb" },
      },
    },

    /** Heading toggle tune */
    HeadingToggleTool: { class: HeadingToggleTool },

    /** Overlay tune (mirrors FontColorTool config) */
    OverlayTool: {
      class: OverlayTool,
      config: {
        options: colorOptions,
        default: slideLayout?.default_font_color || "",
        layout: slideLayout.id,
        blocks: {}, // keep for backward compatibility
      },
    },

    /** Drag/Resize tune */
    DragResize: { class: DragResizeTune },
  };
}

/**
 * Master resolver that builds the full Editor.js config dynamically
 */
export function getEditorConfig(
  slideLayout,
  client,
  ctx,
  theme_json,
  themeDirectory,
  slideConfig
) {
  const layout = slideConfig?.[slideLayout.id] || slideConfig?.default;

  let tools = {};

  if (!layout?.tools) {
    console.error(`No layout tools found for layout ${slideLayout?.id}`);
    return {
      tools: getInlineTools(theme_json, themeDirectory, slideLayout),
      default_block: layout?.default_block,
    };
  }

  // Build tools from JSON layout
  Object.entries(layout.tools).forEach(([key, block]) => {
    if (
      block.conditional?.clients &&
      !block.conditional.clients.includes(client)
    ) {
      console.log('skipping block "' + key + '" for client "' + client + '"');
      return; // Skip block not allowed for this client
    }

    const blockClass = blockRegistry[block.class] || null;

    const tunes = Array.isArray(block.tunes)
      ? block.tunes
      : typeof block.tunes === "object"
      ? block.tunes[client] || block.tunes.default || []
      : block.tunes || [];

    // Start with all static config properties from JSON (spread first)
    // Exclude processing keys (props, refs, handlers, etc.) that aren't meant for blocks
    const {
      props: _,
      defaultContentRef: _dcr,
      optionsRef: _or,
      layoutOptionsRef: _lor,
      handlers: _h,
      handlerOptions: _ho,
      slideFiles: _sf,
      // customFiles: _cf,
      ...staticConfig
    } = block.config || {};
    const config = { ...staticConfig };

    // Resolve JSON references (functions, props, files) from both config root and config.props
    const configRefs = block.config || {};
    const propsRefs = block.config?.props || {};

    if (configRefs.defaultContentRef || propsRefs.defaultContentRef)
      config.defaultContent = resolveRef(
        configRefs.defaultContentRef || propsRefs.defaultContentRef,
        theme_json,
        slideLayout,
        ctx.themeLabel
      );
    if (configRefs.optionsRef || propsRefs.optionsRef)
      config.options = resolveRef(
        configRefs.optionsRef || propsRefs.optionsRef,
        theme_json,
        slideLayout,
        ctx.themeLabel
      );

    // Handle layoutOptionsRef - allows referencing another layout's options
    if (configRefs.layoutOptionsRef || propsRefs.layoutOptionsRef) {
      config.options = theme_json?.layouts?.find((e) => {
        return (
          e.id === (configRefs.layoutOptionsRef || propsRefs.layoutOptionsRef)
        );
      });
    }

    if (block.config?.props) {
      const propDefs = block.config.props;
      if (Array.isArray(propDefs)) {
        const resolvedProps = {};
        propDefs.forEach((prop) => {
          switch (prop) {
            case "googleApiKey":
              resolvedProps.googleApiKey =
                ctx.googleApiKey ?? theme_json?.googleApiKey;
              break;
            case "brandLogos":
              resolvedProps.brandLogos = theme_json.brandLogos ?? {};
              break;
            case "themeDirectory":
              resolvedProps.themeDirectory = ctx.themeDirectory;
              break;
            case "pin":
              resolvedProps.pin = ctx.pin ?? theme_json?.icons?.pin;
              break;
            case "presentationId":
              resolvedProps.presentationId = ctx.presentationId;
              break;
            case "slideId":
              resolvedProps.slideId = ctx.slideId;
              resolvedProps.slide = { id: ctx.slideId };
              break;
            case "layoutOptions":
              // Resolve layout-specific options from theme_json
              resolvedProps.options = theme_json?.layouts?.find((e) => {
                return e.id === slideLayout.id;
              });
              break;
            case "layoutFiles":
              // Resolve layout-specific files from theme_json
              resolvedProps.layoutFiles = theme_json?.layouts?.find((e) => {
                return e.id === slideLayout.id;
              })?.files;
              break;
            default:
              if (ctx?.[prop] !== undefined) {
                resolvedProps[prop] = ctx[prop];
              }
              break;
          }
        });
        Object.assign(config, resolvedProps);
      } else if (typeof propDefs === "object") {
        // Spread props into config, but exclude refs (they're already processed)
        const {
          defaultContentRef,
          optionsRef,
          layoutOptionsRef,
          ...actualProps
        } = propDefs;
        Object.assign(config, actualProps);
      }
    }
    if (config.loader) {
      config.loader = renderToDomNode(<CircularProgress size={20} />);
    }

    // Attach handler functions from JSON
    if (block.config?.handlers) {
      block.config.handlers.forEach((fn) => {
        if (handlers[fn]) {
          const handlerOptions = block.config.handlerOptions?.[fn] || {};
          const handlerFn = (blk) =>
            handlers[fn](blk, ctx, {
              ...handlerOptions,
              layoutId: slideLayout.id,
              blockType: key,
            });
          config[fn] = handlerFn;

          // Ensure media-capable blocks receive showMediaLibrary
          if (fn.includes("MediaLibrary")) {
            config.showMediaLibrary = handlerFn;
          }
        }
      });
    }

    // Fallback defaultContent from legacy helper if not provided in config
    if (
      !config.defaultContent &&
      typeof getDefaultBlockContent === "function"
    ) {
      const fallback = getDefaultBlockContent(slideLayout.id, key, theme_json);
      if (fallback) config.defaultContent = fallback;
    }

    // slideFiles > config-driven media constraints for ANY block
    if (block.config?.slideFiles) {
      config.showMediaLibrary = (blk) => {
        // Resolve dynamic references like "block.aspectRatio" at runtime
        const resolvedSlideFiles = { ...block.config.slideFiles };
        Object.entries(resolvedSlideFiles).forEach(([key, value]) => {
          if (value === "block.aspectRatio" && blk?.aspectRatio) {
            resolvedSlideFiles[key] = blk.aspectRatio;
          }
        });
        ctx.setSlideFiles(resolvedSlideFiles);
        ctx.setCurrentBlock(blk);
        ctx.setShowMediaLibrary(true);
      };
    }

    // Custom files override
    if (block.config?.customFiles) {
      config.showMediaLibrary = (blk) => {
        const files =
          blk?.customFiles ??
          block.config?.customFiles ??
          block.config?.fallbackFiles;

        ctx.setSlideFiles(files);
        ctx.setCurrentBlock(blk);
        ctx.setShowMediaLibrary(true);
      };
    }

    if (ctx.slideId) config.slideId = ctx.slideId;

    tools[key] = {
      class: blockClass,
      tunes,
      ...(block.inlineToolbar ? { inlineToolbar: block.inlineToolbar } : {}),
      ...(Object.keys(config).length ? { config } : {}),
    };
  });

  // Merge theme_json additionalBlocks (theme-specific)
  if (theme_json?.layouts?.length > 0) {
    const foundLayout = theme_json.layouts.find(
      (e) => e.id === slideLayout.id && e.additionalBlocks?.length > 0
    );
    if (foundLayout) {
      // Mirror legacy layout-specific additional block setup
      foundLayout.additionalBlocks.forEach((f) => {
        switch (f.blockName) {
          case "title":
            tools.title = {
              class: Title,
              tunes: ["Animation", "FontColorTool", ...(f.tunes || [])],
            };
            break;
          case "subtitle":
            tools.subtitle = {
              class: Subtitle,
              tunes: ["Animation", "FontColorTool", ...(f.tunes || [])],
            };
            break;
          case "body":
            tools.body = {
              class: Body,
              tunes: ["Animation", "FontColorTool", ...(f.tunes || [])],
              inlineToolbar: [
                "bold",
                "italic",
                "Subscript",
                "Superscript",
                "Hyperlink",
                "SlideLink",
                "PopupLink",
              ],
            };
            break;
          case "list":
            tools.list = {
              class: NestedList,
              tunes: ["Animation", "FontColorTool"],
              inlineToolbar: [
                "bold",
                "italic",
                "Subscript",
                "Superscript",
                "Hyperlink",
                "SlideLink",
                "PopupLink",
              ],
            };
            break;
          case "buttonGroup":
            tools.buttonGroup = {
              class: ButtonGroup,
              tunes: ["AlignmentBlockTune"],
              config: { percentage: true },
            };
            break;
          case "image":
            tools.image = {
              class: Image,
              tunes: ["Animation", "OverlayTool", "DragResize"],
              config: {
                // Respect aspect ratio for sizing and use media library
                dynamicImageSize: true,
                showMediaLibrary: (blk) => handlers.showMediaLibrary(blk, ctx),
              },
            };
            break;
          default:
            tools[f.blockName] = {
              class: blockRegistry[f.class] || Title,
              tunes: ["Animation", "FontColorTool", ...(f.tunes || [])],
            };
            break;
        }
      });
    }
  }

  Object.assign(tools, getInlineTools(theme_json, themeDirectory, slideLayout));

  if (!tools || Object.keys(tools).length === 0) {
    console.error(`No tools found for layout ${slideLayout.id}`);
  }

  return {
    tools,
    default_block: layout.default_block,
  };
}

export function getAvailableTools(slideLayout, slideConfig) {
  const layout = slideConfig?.[slideLayout.id] || slideConfig?.default;
  const availableTools = [];
  if(layout?.tools) {
    const tools = layout.tools;
    Object.keys(tools).forEach((key) => {
      const blockClassName = tools[key].class;
      const blockClass = blockRegistry[blockClassName] || null;
      if(blockClass) {
        availableTools.push({
          key,
          ...blockClass.toolbox
        });
      }
    });
  }
  return availableTools;
}